/**
 * Singleton loader for the remote FF widget CDN scripts (header / footer).
 * Resolves with the widget global (window.FFHeader / window.FFFooter)
 * once the script is available. One in-flight promise per global name.
 */

const loadPromises = {};

/**
 * @param {string} url - Absolute URL of the widget bundle
 * @param {string} globalName - Global the bundle defines, e.g. 'FFHeader'
 * @returns {Promise<object>} the widget global API
 */
export default function loadFFWidgetScript(url, globalName) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('[loadFFWidgetScript] window is not defined'));
  }

  if (!url) {
    return Promise.reject(new Error('[loadFFWidgetScript] script URL is empty'));
  }

  if (window[globalName]) {
    return Promise.resolve(window[globalName]);
  }

  if (loadPromises[globalName]) {
    return loadPromises[globalName];
  }

  loadPromises[globalName] = new Promise((resolve, reject) => {
    const settle = () => {
      if (window[globalName]) {
        resolve(window[globalName]);
      } else {
        loadPromises[globalName] = null;
        reject(new Error(`[loadFFWidgetScript] script loaded but window.${globalName} is missing`));
      }
    };

    const fail = (script, { remove }) => {
      loadPromises[globalName] = null;
      if (remove) {
        script.remove();
      }
      reject(new Error(`[loadFFWidgetScript] failed to load ${url}`));
    };

    const existing = document.querySelector(`script[data-ff-widget="${globalName}"][data-ff-widget-src="${url}"]`);
    if (existing) {
      // Script tag already present; wait for it or resolve if already loaded
      if (window[globalName]) {
        resolve(window[globalName]);
        return;
      }
      existing.addEventListener('load', settle);
      existing.addEventListener('error', () => fail(existing, { remove: false }));
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.dataset.ffWidget = globalName;
    script.dataset.ffWidgetSrc = url;

    script.onload = settle;
    script.onerror = () => fail(script, { remove: true });

    document.head.appendChild(script);
  });

  return loadPromises[globalName];
}

/**
 * @param {string} url - Absolute URL of ff-header.js
 * @returns {Promise<object>} FFHeader global API
 */
export function loadFFHeaderScript(url) {
  return loadFFWidgetScript(url, 'FFHeader');
}

/**
 * @param {string} url - Absolute URL of ff-footer.js
 * @returns {Promise<object>} FFFooter global API
 */
export function loadFFFooterScript(url) {
  return loadFFWidgetScript(url, 'FFFooter');
}
